import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CloudUpload,
  Database,
  Download,
  HardDrive,
  Loader2,
  Play,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { downloadAuthenticatedFile } from '@/lib/download';
import { getErrorMessage } from '@/lib/errors';
import {
  formatBytes,
  minuteToTime,
  platformBackupsApi,
  platformBackupsKey,
  timeToMinute,
  type PlatformBackupRun,
  type PlatformBackupSettings,
} from '@/lib/platform-backups-api';
import { relativeTime } from '@/modules/monitoring/format';

/**
 * The platform's backups of itself.
 *
 * ## Why this page exists
 *
 * The Backups module protects customers' servers. Nothing protected the platform, so the
 * operator's only option was a hand-written cron script — for the one server whose loss takes
 * every customer's inventory, credentials, subscriptions and invoices with it. Being told to write
 * shell scripts to protect the product you bought is a fair thing to object to.
 *
 * ## What it deliberately cannot do
 *
 * **Restore.** If the server is gone, so is this page. Restoring is inherently a command-line
 * act, so the page shows the exact command rather than pretending otherwise — a restore button
 * that only works while nothing is wrong would be theatre.
 */
export function AdminPlatformBackupsPage() {
  const queryClient = useQueryClient();

  const overview = useQuery({
    queryKey: platformBackupsKey,
    queryFn: () => platformBackupsApi.overview(),
    // Polled only while a run is in flight — a dump takes seconds to minutes and the page
    // should show it finish without the operator reaching for reload.
    refetchInterval: (query) => (query.state.data?.running === true ? 3000 : false),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: platformBackupsKey });

  const run = useMutation({ mutationFn: () => platformBackupsApi.run(), onSuccess: refresh });

  const data = overview.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Platform backups</h1>
          <p className="text-sm text-muted-foreground">
            This server&rsquo;s own database and uploaded files. Separate from the backups you
            run for customers.
          </p>
        </div>
        <Button className="ml-auto" onClick={() => run.mutate()} disabled={run.isPending || data?.running === true}>
          {run.isPending || data?.running === true ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <Play className="mr-2 size-4" />
          )}
          {data?.running === true ? 'Running…' : 'Back up now'}
        </Button>
      </div>

      {run.isError && <Alert variant="danger">{getErrorMessage(run.error)}</Alert>}
      {overview.isError && <Alert variant="danger">{getErrorMessage(overview.error)}</Alert>}

      {overview.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </p>
      )}

      {data !== undefined && (
        <>
          <Status data={data} />
          <SettingsCard settings={data.settings} onSaved={refresh} />
          <History backups={data.backups} />
          <RestoreNote />
        </>
      )}
    </div>
  );
}

/**
 * The one number that matters, said plainly.
 *
 * Not "last backup: 2026-08-01T03:30:00Z" — a date the reader has to do arithmetic on. The
 * question is *how long have I been unprotected*, so the page answers that.
 */
function Status({ data }: { data: { last_successful_at: string | null; days_since_success: number | null; settings: PlatformBackupSettings } }) {
  const { last_successful_at: last, days_since_success: days, settings } = data;

  if (last === null) {
    return (
      <Alert variant="danger">
        No backup has ever completed. Nothing on this server is protected.
      </Alert>
    );
  }

  const stale = days !== null && days >= 2;

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex size-10 items-center justify-center rounded-xl ${
              stale ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success'
            }`}
          >
            {stale ? <AlertTriangle className="size-5" /> : <ShieldCheck className="size-5" />}
          </span>
          <div>
            <p className="font-medium">Last successful backup {relativeTime(last)}</p>
            <p className="text-sm text-muted-foreground">
              {settings.enabled
                ? `Nightly at ${minuteToTime(settings.run_at_minute)} UTC, kept ${settings.keep_days} days`
                : 'The nightly schedule is off. Backups only run when you press the button'}
            </p>
          </div>
        </div>

        <Badge variant={settings.s3_enabled ? 'success' : 'warning'}>
          {settings.s3_enabled ? 'copied off-site' : 'on this server only'}
        </Badge>
      </div>

      {/* Said out loud rather than left to be noticed. A backup that only exists on the machine
          it is a backup of does not survive the failure it exists for. */}
      {!settings.s3_enabled && (
        <p className="mt-4 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          These backups are on the same server they protect. If you lose the machine you lose
          them too. Configure off-site copies below, or download them regularly.
        </p>
      )}
    </Card>
  );
}

function SettingsCard({
  settings,
  onSaved,
}: {
  settings: PlatformBackupSettings;
  onSaved: () => void;
}) {
  const [enabled, setEnabled] = useState(settings.enabled);
  const [time, setTime] = useState(minuteToTime(settings.run_at_minute));
  const [keepDays, setKeepDays] = useState(String(settings.keep_days));
  const [s3Enabled, setS3Enabled] = useState(settings.s3_enabled);
  const [endpoint, setEndpoint] = useState(settings.s3_endpoint);
  const [region, setRegion] = useState(settings.s3_region);
  const [bucket, setBucket] = useState(settings.s3_bucket);
  const [prefix, setPrefix] = useState(settings.s3_prefix);
  const [accessKey, setAccessKey] = useState(settings.s3_access_key_id);
  const [secret, setSecret] = useState('');

  // Re-sync when a save returns fresh values, so the form reflects what was stored rather
  // than what was typed — they differ whenever the server normalises something.
  useEffect(() => {
    setEnabled(settings.enabled);
    setTime(minuteToTime(settings.run_at_minute));
    setKeepDays(String(settings.keep_days));
    setS3Enabled(settings.s3_enabled);
    setEndpoint(settings.s3_endpoint);
    setRegion(settings.s3_region);
    setBucket(settings.s3_bucket);
    setPrefix(settings.s3_prefix);
    setAccessKey(settings.s3_access_key_id);
    setSecret('');
  }, [settings]);

  const minute = timeToMinute(time);
  const keep = Number.parseInt(keepDays, 10);

  const problem = (() => {
    if (minute === null) return 'Time must look like 03:30.';
    if (!Number.isFinite(keep) || keep < 1 || keep > 3650) return 'Keep between 1 and 3650 days.';
    if (s3Enabled && bucket.trim() === '') return 'Off-site copies need a bucket name.';
    if (s3Enabled && endpoint.trim() === '') return 'Off-site copies need an endpoint.';
    if (s3Enabled && accessKey.trim() === '') return 'Off-site copies need an access key.';
    if (s3Enabled && !settings.s3_secret_set && secret.trim() === '') {
      return 'Off-site copies need a secret key.';
    }

    return null;
  })();

  const save = useMutation({
    mutationFn: () =>
      platformBackupsApi.saveSettings({
        enabled,
        run_at_minute: minute ?? 210,
        keep_days: keep,
        s3_enabled: s3Enabled,
        s3_endpoint: endpoint.trim(),
        s3_region: region.trim(),
        s3_bucket: bucket.trim(),
        s3_prefix: prefix.trim(),
        s3_access_key_id: accessKey.trim(),
        // Omitted when blank: the stored secret cannot be read back to prefill the field, so
        // an empty box is the normal state when editing anything else on this form.
        ...(secret.trim() === '' ? {} : { s3_secret_key: secret.trim() }),
      }),
    onSuccess: onSaved,
  });

  return (
    <Card className="space-y-5 p-6">
      <h2 className="text-lg font-semibold tracking-tight">Schedule</h2>

      {save.isError && <Alert variant="danger">{getErrorMessage(save.error)}</Alert>}

      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="size-4"
        />
        Back up automatically every night
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="pb-time">Time (UTC)</Label>
          <Input id="pb-time" value={time} onChange={(e) => setTime(e.target.value)} disabled={!enabled} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pb-keep">Keep for (days)</Label>
          <Input id="pb-keep" value={keepDays} onChange={(e) => setKeepDays(e.target.value)} inputMode="numeric" />
          <p className="text-xs text-muted-foreground">
            Older backups are deleted from this server. Anything already copied off-site stays.
          </p>
        </div>
      </div>

      <div className="border-t pt-5">
        <h2 className="mb-1 text-lg font-semibold tracking-tight">Off-site copies</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Any S3-compatible storage: Backblaze B2, Wasabi, DigitalOcean Spaces, MinIO, AWS.
          Each backup uploads automatically after it runs.
        </p>

        <label className="mb-4 flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={s3Enabled}
            onChange={(e) => setS3Enabled(e.target.checked)}
            className="size-4"
          />
          Copy every backup to S3-compatible storage
        </label>

        {s3Enabled && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pb-endpoint">Endpoint</Label>
                <Input id="pb-endpoint" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} className="font-mono text-sm" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pb-region">Region</Label>
                <Input id="pb-region" value={region} onChange={(e) => setRegion(e.target.value)} className="font-mono text-sm" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pb-bucket">Bucket</Label>
                <Input id="pb-bucket" value={bucket} onChange={(e) => setBucket(e.target.value)} className="font-mono text-sm" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pb-prefix">Folder <span className="font-normal text-muted-foreground">(optional)</span></Label>
                <Input id="pb-prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)} className="font-mono text-sm" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pb-key">Access key ID</Label>
                <Input id="pb-key" value={accessKey} onChange={(e) => setAccessKey(e.target.value)} className="font-mono text-sm" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pb-secret">
                  Secret key{' '}
                  {settings.s3_secret_set && (
                    <span className="font-normal text-muted-foreground">(stored, leave blank to keep)</span>
                  )}
                </Label>
                <Input
                  id="pb-secret"
                  type="password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* The same rule every credential on this platform follows, said where somebody
                is about to type one. */}
            <p className="text-xs text-muted-foreground">
              Encrypted before storage and never sent back to this page.
            </p>
          </div>
        )}
      </div>

      {problem !== null && <Alert variant="danger">{problem}</Alert>}

      <Button onClick={() => save.mutate()} disabled={save.isPending || problem !== null}>
        {save.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
        Save settings
      </Button>
    </Card>
  );
}

function History({ backups }: { backups: PlatformBackupRun[] }) {
  if (backups.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        No backups yet.
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="mb-1 text-lg font-semibold tracking-tight">Recent backups</h2>
      {/* Failures stay in this list on purpose — a list of successes only would hide the one
          thing you need to see, which is that the last four nights did not work. */}
      <p className="mb-4 text-sm text-muted-foreground">
        Failed runs are kept here deliberately. A run of failures is what you need to notice.
      </p>

      <div className="space-y-2">
        {backups.map((backup) => (
          <Row key={backup.id} backup={backup} />
        ))}
      </div>
    </Card>
  );
}

function Row({ backup }: { backup: PlatformBackupRun }) {
  const [busy, setBusy] = useState<'database' | 'uploads' | null>(null);

  const download = async (file: 'database' | 'uploads') => {
    setBusy(file);
    try {
      await downloadAuthenticatedFile(
        platformBackupsApi.downloadPath(backup.id, file),
        file === 'uploads' ? 'uploads.tar.gz' : 'database.sql.gz',
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/20 px-4 py-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{relativeTime(backup.started_at)}</span>
          {backup.status === 'succeeded' && <Badge variant="success">succeeded</Badge>}
          {backup.status === 'failed' && <Badge variant="danger">failed</Badge>}
          {backup.status === 'running' && <Badge variant="warning">running</Badge>}
          {backup.manual && <Badge variant="muted">manual</Badge>}
          {backup.status === 'succeeded' && backup.offsite && (
            <span className="inline-flex items-center gap-1 text-xs text-success">
              <CloudUpload className="size-3" /> off-site
            </span>
          )}
        </div>

        {backup.status === 'succeeded' && (
          <p className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Database className="size-3" /> {formatBytes(backup.database_bytes)}
            </span>
            <span className="inline-flex items-center gap-1">
              <HardDrive className="size-3" /> {formatBytes(backup.uploads_bytes)}
            </span>
          </p>
        )}

        {backup.error !== '' && (
          <p className="mt-1 max-w-xl break-words text-xs text-danger">{backup.error}</p>
        )}

        {/* A run that dumped fine but could not reach S3 is not a failure — the backup
            exists — and is not a whole success either. Shown as its own line rather than
            rounded to one or the other. */}
        {backup.offsite_error !== '' && (
          <p className="mt-1 max-w-xl break-words text-xs text-warning">
            Off-site copy failed: {backup.offsite_error}
          </p>
        )}
      </div>

      {backup.status === 'succeeded' && (
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" disabled={busy !== null} onClick={() => void download('database')}>
            {busy === 'database' ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : <Download className="mr-1.5 size-3.5" />}
            Database
          </Button>
          <Button variant="ghost" size="sm" disabled={busy !== null} onClick={() => void download('uploads')}>
            {busy === 'uploads' ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : <Download className="mr-1.5 size-3.5" />}
            Files
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Restoring cannot be a button, and saying so is more useful than hiding it.
 */
function RestoreNote() {
  return (
    <Card className="p-6">
      <h2 className="mb-1 text-lg font-semibold tracking-tight">Restoring</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        There is no restore button by design: if this server is gone, so is this page. Restoring
        is a documented manual procedure on a fresh machine.
      </p>

      <p className="mb-2 text-sm font-medium">Restore the database</p>
      <pre className="overflow-x-auto rounded-md border bg-muted/30 p-3 text-xs">
        <code>{`gunzip -c database.sql.gz | docker compose exec -T postgres psql -U iap -d iap`}</code>
      </pre>

      <p className="mb-2 mt-4 text-sm font-medium">Restore uploaded files</p>
      <pre className="overflow-x-auto rounded-md border bg-muted/30 p-3 text-xs">
        <code>{`docker compose exec -T php tar -xzf - -C /app/var/uploads < uploads.tar.gz`}</code>
      </pre>

      <Alert variant="warning" className="mt-4">
        Restoring the database also needs <code className="font-mono">APP_ENCRYPTION_KEY</code>{' '}
        from the <code className="font-mono">.env</code> of the install the backup came from.
        Without it every stored SSH credential and two-factor secret is unreadable, and no part
        of this backup can recover them.
      </Alert>
    </Card>
  );
}
